import { useState, useRef, useEffect } from 'react';
import { Paperclip, Mic, StopCircle, Palette, X } from 'lucide-react';
import useVoiceRecording from '../chatbotScreen/hooks/useVoiceRecording';
import useSocketConnection from '../chatbotScreen/hooks/useSocketConnection';
import promptInputService from './api';
import RendererTest from '../../screens/RendererTest';

const PromptInput = () => {
  const fileInputRef = useRef(null);
  const styleFileInputRef = useRef(null);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState([]);
  const [styleRefFile, setStyleRefFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    sessionStorage.removeItem('tailwindConfig');
  }, []);

  const { socketRef, transcriptionLogRef, transcriptionTextRef } = useSocketConnection(setInput, setIsRecording);
  const { handleStartRecording, handleStopRecording } = useVoiceRecording(
    socketRef, transcriptionLogRef, transcriptionTextRef, isRecording, setIsRecording
  );

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files);
    const validation = promptInputService.validateFiles(uploadedFiles);
    if (!validation.isValid) { setError(validation.errors.join('\n')); return; }
    setFiles([...files, ...uploadedFiles]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleStyleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Style reference must be an image file (PNG, JPG, etc.)');
      return;
    }
    setStyleRefFile(file);
    setError(null);
    if (styleFileInputRef.current) styleFileInputRef.current.value = '';
  };

  const handleSendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput && files.length === 0) {
      setError('Please enter a prompt or upload an image before sending.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await promptInputService.sendPrompt(trimmedInput, files, styleRefFile);
      setHasGenerated(true);
      setRenderKey((k) => k + 1);
      setInput('');
      setFiles([]);
      setStyleRefFile(null);
    } catch (err) {
      setError(err.message || 'Failed to send prompt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
      {/* Page header */}
      {/* <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">UI Renderer Prompt Input</h1>
        <p className="text-[14px] text-gray-600 mt-1">Send prompts directly from the settings panel. Use the prompt input to test and interact with your AI agents.</p>
      </div> */}

      {error && (
        <div className="mb-4 bg-red-50 px-4 py-3 rounded-lg border border-red-200">
          <p className="text-sm text-red-700 font-medium">{error}</p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="flex gap-3 h-[calc(100vh-180px)]">

        {/* Left card */}
        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 p-4 transition-all duration-500 flex flex-col h-full ${hasGenerated || isSubmitting ? 'w-[320px] flex-shrink-0' : 'flex-1'}`}>
          {/* Heading + info icon */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="font-bold text-xl text-primary tracking-wider">NOVA</span>
              <span className="text-xl font-light text-gray-700">Prompt Input</span>
            </div>
            <div className="relative" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
              <div className="w-8 h-8 rounded-full border-2 border-primary/30 flex items-center justify-center cursor-pointer hover:border-primary transition-colors">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="8" strokeLinecap="round" strokeWidth="2.5" />
                  <line x1="12" y1="12" x2="12" y2="16" strokeLinecap="round" strokeWidth="2" />
                </svg>
              </div>
              {showTooltip && (
                <div className="absolute right-0 top-10 z-50 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                  <p className="text-primary font-semibold text-sm mb-2">Features</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span><span>Type your prompts in the textbox to render your UI page using the AI agent</span></li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span><span>Upload files (images, HTML code, documents) using the paperclip icon for specific UI rendering tasks</span></li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px] mb-1">What is your UI for?</h2>
          <p className="text-xs text-gray-500 mb-3">Describe the use case. Optionally upload an architecture diagram and/or a style reference.</p>

          {/* Hidden file inputs */}
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" multiple />
          <input type="file" ref={styleFileInputRef} onChange={handleStyleFileUpload} className="hidden" accept="image/*" />

          {/* Prompt textarea */}
          <div className={`mb-3 rounded-lg overflow-hidden bg-white border transition-all duration-300 ease-in-out focus-within:border-primary focus-within:ring-1 focus-within:ring-primary ${isRecording ? 'border-red-300' : 'border-gray-200'}`}>
            {/* Uploaded file chips shown above textarea */}
            {(files.length > 0 || styleRefFile) && (
              <div className="flex flex-wrap gap-1.5 px-3 pt-2.5">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[11px] font-medium px-2 py-0.5 rounded-full border border-blue-200">
                    <Paperclip size={10} />
                    <span className="max-w-[110px] truncate">{f.name}</span>
                    <button onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="ml-0.5 text-blue-400 hover:text-blue-700"><X size={10} /></button>
                  </span>
                ))}
                {styleRefFile && (
                  <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-[11px] font-medium px-2 py-0.5 rounded-full border border-purple-200">
                    <Palette size={10} />
                    <span className="max-w-[110px] truncate">{styleRefFile.name}</span>
                    <button onClick={() => setStyleRefFile(null)} className="ml-0.5 text-purple-400 hover:text-purple-700"><X size={10} /></button>
                  </span>
                )}
              </div>
            )}
            <textarea
              rows={5}
              className={`w-full resize-none bg-white px-4 pt-3 pb-3 text-sm text-gray-800 outline-none focus:outline-none focus:ring-0 focus:shadow-none placeholder-[#aaa] ${isRecording ? 'text-red-500' : ''}`}
              placeholder={isRecording ? 'Listening...' : 'Describe the UI purpose or additional instructions…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <div className="flex items-center justify-end gap-1 px-3 pb-2 pt-1 border-t border-gray-100">
              <button className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors" onClick={() => fileInputRef.current?.click()} type="button" title="Upload architecture diagram">
                <Paperclip size={15} />
              </button>
              <button className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-purple-500 hover:bg-purple-50 transition-colors" onClick={() => styleFileInputRef.current?.click()} type="button" title="Upload style reference">
                <Palette size={15} />
              </button>
              <button className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors ${isRecording ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-gray-400 hover:text-primary hover:bg-blue-50'}`} onClick={isRecording ? handleStopRecording : handleStartRecording} type="button" title={isRecording ? 'Stop recording' : 'Start voice input'}>
                {isRecording ? <StopCircle size={15} /> : <Mic size={15} />}
              </button>
            </div>
          </div>

          <button onClick={handleSendMessage} disabled={isSubmitting} className="w-full h-10 px-4 py-2 rounded-lg font-semibold text-white text-sm bg-secondary hover:bg-secondaryDark disabled:opacity-60 disabled:cursor-not-allowed transition-all">
            {isSubmitting ? 'Generating…' : 'Generate UI Prompt'}
          </button>

          <p className="mt-3 text-center text-xs text-gray-400">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[11px] font-mono">Enter</kbd> to send • <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[11px] font-mono">Shift + Enter</kbd> for new line
          </p>
        </div>

        {/* Right card */}
        <div className="flex-1 min-w-0 bg-white rounded-lg shadow-lg border border-gray-200 p-4 flex flex-col transition-all duration-500 h-full overflow-hidden">
          {!isSubmitting && !hasGenerated && (
            <h2 className="text-[14px] font-semibold text-gray-600 uppercase tracking-[0.7px] mb-4">Generated UI Prompt</h2>
          )}
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex-1 min-h-0 overflow-y-auto">
            {isSubmitting ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-sm text-gray-400 animate-pulse">Generating…</p>
              </div>
            ) : hasGenerated ? (
              <div className="p-4 h-full">
                <RendererTest key={renderKey} />
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-sm text-gray-400">Your generated UI prompt will appear here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptInput;
